// src/pages/HomePage.js
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import TigerLogo from "../components/common/TigerLogo";
import {
  IconBasketball, IconBaseball, IconFootball, IconSoccer,
  IconTrack, IconSwimming, IconVolleyball,
  IconUsers, IconShield, IconLayers,
} from "../components/common/Icons";

const SPORTS = [
  { name: "Basketball", path: "/sports/basketball", icon: IconBasketball, color: "from-orange-500 to-amber-600", season: "Winter" },
  { name: "Baseball", path: "/sports/baseball", icon: IconBaseball, color: "from-red-500 to-rose-600", season: "Spring" },
  { name: "Football", path: "/sports/football", icon: IconFootball, color: "from-green-500 to-emerald-600", season: "Fall" },
  { name: "Soccer", path: "/sports/soccer", icon: IconSoccer, color: "from-blue-500 to-sky-600", season: "Fall" },
  { name: "Track & Field", path: "/sports/track", icon: IconTrack, color: "from-purple-500 to-violet-600", season: "Spring" },
  { name: "Swimming", path: "/sports/swimming", icon: IconSwimming, color: "from-cyan-500 to-teal-600", season: "Winter" },
  { name: "Volleyball", path: "/sports/volleyball", icon: IconVolleyball, color: "from-pink-500 to-fuchsia-600", season: "Fall" },
];

export default function HomePage() {
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission("manage_users");

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 stripe-pattern" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-tiger-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />

        <div className="relative max-w-7xl mx-auto px-4 py-20 text-center">
          <div className="flex justify-center mb-6 animate-fade-in-up">
            <div className="relative">
              <TigerLogo size={120} />
              <div className="absolute inset-0 bg-tiger-500/10 rounded-full blur-2xl scale-150" />
            </div>
          </div>

          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-wider animate-fade-in-up stagger-1">
            <span className="text-tiger-400">EVANDER</span>{" "}
            <span className="text-onyx-100">CAMPUS</span>
          </h1>
          <p className="font-display text-xl md:text-2xl text-onyx-400 tracking-widest mt-3 animate-fade-in-up stagger-2">
            PSAL ATHLETICS
          </p>
          <p className="font-body text-onyx-500 mt-4 max-w-lg mx-auto animate-fade-in-up stagger-3">
            Home of the Tigers. Compete with heart, win with pride.
          </p>

          {user && (
            <div className="mt-8 inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-onyx-900/60 border border-onyx-800/50 animate-fade-in-up stagger-4">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-tiger-500 to-tiger-700 flex items-center justify-center">
                <span className="text-sm font-display font-bold text-white">
                  {user.username?.[0]?.toUpperCase()}
                </span>
              </div>
              <span className="font-body text-onyx-200">Welcome, {user.username}</span>
              <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-tiger-500/15 text-tiger-400 border border-tiger-500/20">
                Level {user.level}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Sports Grid */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="font-display text-lg font-semibold text-onyx-300 tracking-widest uppercase mb-8">
          Sports Programs
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {SPORTS.map((sport, i) => {
            const Icon = sport.icon;
            return (
              <Link
                key={sport.path}
                to={sport.path}
                className="group relative overflow-hidden rounded-xl bg-onyx-900/60 border border-onyx-800/40
                  hover:border-tiger-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-tiger-500/5"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="p-5">
                  <div className={`inline-flex p-2.5 rounded-lg bg-gradient-to-br ${sport.color} mb-4
                    group-hover:scale-110 transition-transform duration-300`}>
                    <Icon size={22} stroke="white" />
                  </div>
                  <h3 className="font-display text-base font-semibold text-onyx-100 tracking-wide">
                    {sport.name.toUpperCase()}
                  </h3>
                  <p className="text-xs font-body text-onyx-500 mt-1">{sport.season} Season</p>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-tiger-500/50 to-transparent
                  opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            );
          })}
        </div>
      </section>

      {/* Admin Quick Links */}
      {canManage && (
        <section className="max-w-7xl mx-auto px-4 pb-16">
          <h2 className="font-display text-lg font-semibold text-onyx-300 tracking-widest uppercase mb-8">
            Administration
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { name: "Users", desc: "Manage accounts & levels", path: "/admin/users", Icon: IconUsers },
              { name: "Permissions", desc: "Create & assign permissions", path: "/admin/permissions", Icon: IconShield },
              { name: "Groups", desc: "Permission groups", path: "/admin/groups", Icon: IconLayers },
            ].map(({ name, desc, path, Icon }) => (
              <Link
                key={path}
                to={path}
                className="flex items-center gap-4 p-5 rounded-xl bg-onyx-900/40 border border-onyx-800/30
                  hover:border-tiger-500/30 hover:bg-onyx-900/60 transition-all group"
              >
                <div className="p-2.5 rounded-lg bg-tiger-500/10 text-tiger-500 group-hover:bg-tiger-500/15 transition-colors">
                  <Icon size={20} />
                </div>
                <div>
                  <p className="font-display font-semibold text-onyx-200 tracking-wide">{name}</p>
                  <p className="text-xs font-body text-onyx-500 mt-0.5">{desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
